import { pluralizeString } from '@elementary/common-ui/utils/common';
import { formatRelativeTime } from '@elementary/common-ui/utils/datetime';
import {
  CONNECTION_ERROR_MESSAGES,
  RUNTIME_TOKEN_FILE_PATH,
  WAREHOUSE_CREDENTIAL_PLACEHOLDERS,
} from './constants';
import {
  Runtime,
  RuntimeConnection,
  RuntimeStatusAlert,
  UpdateRuntimeRequest,
} from './types';

export const toUpdateRuntimeRequest = (
  runtime: Runtime,
): UpdateRuntimeRequest => ({
  name: runtime.name,
  adminStatus: runtime.adminStatus,
  warehouseConnectionIds: runtime.connections.map(
    (connection) => connection.id,
  ),
});

export const getConnectionErrorMessage = (
  connection: RuntimeConnection,
): string | null =>
  connection.errorCode
    ? CONNECTION_ERROR_MESSAGES[connection.errorCode] ?? connection.errorCode
    : null;

export const getRuntimeActivityLabel = (runtime: Runtime): string => {
  if (!runtime.lastSeenAt) {
    return 'Never connected';
  }
  const lastActivity = `Last activity ${formatRelativeTime(
    runtime.lastSeenAt,
  )}`;
  return `${lastActivity} · ${pluralizeString(
    runtime.onlineInstanceCount,
    'instance',
    'instances',
  )} online`;
};

export const getRuntimeStatusAlert = (
  runtime: Runtime,
): RuntimeStatusAlert | null => {
  if (runtime.health === 'pending') {
    return {
      severity: 'info',
      title: 'Waiting for the runtime to connect',
      message:
        'Add warehouses, generate a token, and start the runtime in your environment with the configuration below.',
    };
  }

  if (runtime.health === 'offline') {
    const lastActivity = formatRelativeTime(runtime.lastSeenAt);
    return {
      severity: 'error',
      title: 'Runtime is offline',
      message: runtime.hasActiveToken
        ? `Last activity was ${lastActivity}. Check that the runtime process is still running in your environment.`
        : `Last activity was ${lastActivity}, and this runtime has no active token. Generate a new token and update the runtime configuration.`,
    };
  }

  if (runtime.health === 'issue') {
    const failedConnections = runtime.connections.filter(
      (connection) => connection.health === 'failed',
    );
    if (failedConnections.length > 0) {
      const details = failedConnections
        .map(
          (connection) =>
            `${connection.name}: ${
              getConnectionErrorMessage(connection) ?? 'Unknown error.'
            }`,
        )
        .join('\n');
      return {
        severity: 'warning',
        title: `${pluralizeString(
          failedConnections.length,
          'warehouse',
          'warehouses',
        )} failed verification`,
        message: `${details}\nCheck the credentials for these warehouses in the runtime configuration.`,
      };
    }

    const pendingConnections = runtime.connections.filter(
      (connection) => connection.health === 'pending',
    );
    if (pendingConnections.length > 0) {
      return {
        severity: 'warning',
        title: `${pluralizeString(
          pendingConnections.length,
          'warehouse is',
          'warehouses are',
        )} waiting for verification`,
        message:
          'Update the runtime configuration and restart it so it can verify the new warehouses.',
      };
    }

    return {
      severity: 'warning',
      title: 'No warehouses assigned',
      message: 'Add a warehouse so this runtime has queries to run.',
    };
  }

  return null;
};

export const buildRuntimeConfigYaml = (runtime: Runtime): string => {
  const connectionLines =
    runtime.connections.length === 0
      ? [
          'connections: []',
          '# Add warehouses to this runtime to generate their entries.',
        ]
      : [
          'connections:',
          ...runtime.connections.flatMap((connection) => [
            `  # ${connection.name}`,
            `  - warehouse_connection_id: ${connection.id}`,
            `    type: ${connection.warehouseType}`,
            ...(
              WAREHOUSE_CREDENTIAL_PLACEHOLDERS[connection.warehouseType] ?? []
            ).map(([key, value]) => `    ${key}: ${value}`),
          ]),
        ];

  return [
    `# Elementary Runtime configuration for "${runtime.name}"`,
    '# Warehouse credentials stay in your environment and are never sent to Elementary.',
    'version: 1',
    'cloud:',
    `  token_file: ${RUNTIME_TOKEN_FILE_PATH}`,
    'limits:',
    '  max_query_timeout_seconds: 300',
    '  max_result_bytes: 10485760',
    '  max_sample_rows_returned: 1000',
    'concurrency:',
    '  max_concurrent_tasks: 8',
    '  heartbeat_interval_seconds: 30',
    ...connectionLines,
  ].join('\n');
};
